import { SUBSCRIPTION_STATUS } from "@/domains/subscriptions/models/constants";
import type { SubscriptionStatus } from "@/domains/subscriptions/models/types";
import { findCatalogProductByVariantId, toDollars } from "@/mocks/data/catalog";
import {
  getSellingPlanNumericId,
  MOCK_MONTHLY_SELLING_PLAN,
  type MockSellingPlan,
} from "@/mocks/data/shopify";
import { isoDaysFromNow } from "@/mocks/dates";
import type { SubscriptionProductWire, SubscriptionWire } from "@/mocks/types";

export type MockSubscriptionLine = {
  variantId: string;
  quantity: number;
};

export const MOCK_SUBSCRIPTION_LINK = "https://subscriptions.eo-design.test/manage";

const MOCK_SHIPPING_ADDRESS = "100 Example Street, Suite 4, Sample City, CA 90000";

const MOCK_PAYMENT_METHOD = {
  cardBrand: "visa",
  expiryDate: "12/29",
  lastDigits: "4242",
};

const roundToCents = (amount: number) => {
  return Math.round(amount * 100) / 100;
};

const createSubscriptionProduct = (
  line: MockSubscriptionLine,
  index: number,
  sellingPlan: MockSellingPlan,
): SubscriptionProductWire[] => {
  const product = findCatalogProductByVariantId(line.variantId);

  if (!product) {
    return [];
  }

  const price = toDollars(product.priceInCents);

  return [
    {
      id: 8000 + product.productId * 10 + index,
      variantId: product.variantId,
      title: product.name,
      sku: product.sku,
      url: product.images[0],
      price,
      finalPrice: roundToCents(price * (1 - sellingPlan.discountPercentage / 100)),
      quantity: line.quantity,
      sellingPlanId: getSellingPlanNumericId(sellingPlan),
    },
  ];
};

type CreateSubscriptionOptions = {
  status: SubscriptionStatus;
  lines: MockSubscriptionLine[];
  acceptedAt?: string | null;
  nextBillingDate?: string | null;
  paymentMethod?: SubscriptionWire["paymentMethod"];
};

export const createSubscription = ({
  acceptedAt = isoDaysFromNow(-30),
  lines,
  nextBillingDate = isoDaysFromNow(12),
  paymentMethod = MOCK_PAYMENT_METHOD,
  status,
}: CreateSubscriptionOptions): SubscriptionWire => {
  return {
    status,
    acceptedAt,
    rejectedAt: null,
    nextBillingDate: status === SUBSCRIPTION_STATUS.ACTIVE ? nextBillingDate : null,
    shippingAddress: MOCK_SHIPPING_ADDRESS,
    paymentMethod,
    products: lines.flatMap((line, index) => {
      return createSubscriptionProduct(line, index, MOCK_MONTHLY_SELLING_PLAN);
    }),
  };
};
